// Formatting/translation helpers for the Auditoria screen (AuditoriaLayer).
// Ported from sge_infantil_web/src/pages/auditing/index.js (the old antd-based
// audit trail page) — same field-label dictionary, value maps and diffing
// rules, just split out of the UI component and stripped of antd/moment.

import { getSucursalVocab } from "./classesLabels";

// Entities as they come back from sge_infantil_core's routes/auditing.js
// (TABLE_ENTITY_MAP) — literal Portuguese business terms, not UI chrome, so
// (like ListaNotasLayer's PERIOD_OPTIONS) they're intentionally not run
// through i18n's t().
export const AUDIT_ENTITIES = [
  "Pagamento",
  "Estudante",
  "Inscrição",
  "Professor",
  "Turma",
  "Disciplina",
  "Frequência",
  "Avaliação",
  "Caderneta",
  "Presença",
  "Matrícula",
  "Configuração de Multa",
  "Multas Aplicadas",
  "Taxa",
  "Emolumentos",
  "Pagamento de Emolumentos",
  "Despesa",
  "Amortização",
  "Produto",
  "Material",
  "Uniforme",
  "Venda de Uniforme",
  "Factura",
  "Plano de Pagamento",
  "Fórmula",
  "Requisição",
  "Pré-inscrição",
  "Consumo",
  "Unidade Base",
  "Tipo de Embalagem",
  "Tipo de Despesa",
  "Login",
  "Configuração de Pagamento",
  "Reavaliação",
  "Config. Actividades Extra",
  "Vendas",
  "Utilizador",
  "Sucursal",
  "Perfil",
];

export const OPERATION_BADGE = {
  insert: "bg-success-focus text-success-main",
  update: "bg-info-focus text-info-main",
  delete: "bg-danger-focus text-danger-main",
};

export const OPERATION_LABEL_KEY = {
  insert: "auditoria.operation.insert",
  update: "auditoria.operation.update",
  delete: "auditoria.operation.delete",
};

// ── Fields considered internal/technical — hidden from the basic insert view ──
const INTERNAL_FIELD_PREFIXES = ["pk", "sk", "gsi"];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalize(key) {
  return key.toLowerCase().replace(/[_\-\s]/g, "");
}

// ── Portuguese labels for known field names ───────────────────────────────────
const FIELD_LABELS = {
  status: "Estado",
  paymentmethod: "Método de Pagamento",
  paymentdate: "Data de Pagamento",
  paidat: "Pago Em",
  forgivefine: "Perdoar Multa",
  hasfine: "Tem Multa",
  fineamount: "Valor da Multa",
  fineforgivenby: "Perdoado Por",
  fineforgivendate: "Data do Perdão",
  amount: "Valor (MT)",
  total: "Total (MT)",
  totalamount: "Total (MT)",
  monthlypayment: "Mensalidade (MT)",
  registrationamount: "Valor de Inscrição (MT)",
  renewalamount: "Valor de Renovação (MT)",
  month: "Mês",
  year: "Ano",
  reference: "Referência",
  limitdate: "Data Limite",
  overdueamount: "Valor em Dívida (MT)",
  overduedamount: "Valor em Dívida (MT)",
  discount: "Desconto (%)",
  name: "Nome",
  birthdate: "Data de Nascimento",
  sex: "Sexo",
  gender: "Sexo",
  mobile: "Contacto",
  address: "Morada",
  level: "Nível",
  corsenumber: "Nível/Curso",
  courseid: "Curso",
  classid: "Turma",
  classroom: "Turma",
  newcomer: "Novo Ingresso",
  allergicfood: "Alérgico a Comida",
  allergicmedicine: "Alérgico a Medicamentos",
  foodlist: "Lista de Alimentos",
  medicinelist: "Lista de Medicamentos",
  hasdisease: "Tem Doença",
  disease: "Doença",
  fathername: "Nome do Pai",
  mothername: "Nome da Mãe",
  fathercontact: "Contacto do Pai",
  mothercontact: "Contacto da Mãe",
  sponsorname: "Nome do Encarregado",
  sponsorcontact: "Contacto do Encarregado",
  kinshipdegree: "Grau de Parentesco",
  contact: "Contacto",
  morada: "Morada",
  doctype: "Tipo de Documento",
  cariername: "Nome do Encarregado",
  cariercontact: "Contacto do Encarregado",
  carier: "Encarregado",
  corse: "Curso/Nível",
  nivel: "Nível",
  alergictomedicine: "Alérgico a Medicamento",
  alergictofood: "Alérgico a Alimentos",
  inscricao: "Inscrição",
  sucursalid: "Escola (ID)",
  activatedby: "Activado Por",
  currentmonthlypayment: "Mensalidade Actual (MT)",
  description: "Descrição",
  notes: "Observações",
  observations: "Observações",
  reason: "Motivo",
  active: "Activo",
  enabled: "Activo",
  approved: "Aprovado",
  approvedby: "Aprovado Por",
  approvedat: "Aprovado Em",
  rejectedby: "Rejeitado Por",
  rejectedat: "Rejeitado Em",
  paid: "Pago",
  paidby: "Pago Por",
  inactivatedat: "Data de Inactivação",
  inactivationreason: "Motivo de Inactivação",
  reactivatedat: "Data de Reactivação",
  createdat: "Criado Em",
  updatedat: "Actualizado Em",
  createdby: "Criado Por",
  updatedby: "Actualizado Por",
  quantity: "Quantidade",
  unitprice: "Preço Unitário (MT)",
  startdate: "Data de Início",
  enddate: "Data de Fim",
  date: "Data",
  type: "Tipo",
  category: "Categoria",
  email: "Email",
  role: "Perfil",
  username: "Utilizador",
  code: "Código",
  size: "Tamanho",
  color: "Cor",
  brand: "Marca",
  subject: "Disciplina",
  subjectid: "Disciplina",
  teacherid: "Professor",
  enrollmentid: "Matrícula",
  registrationid: "Inscrição",
};

// ── Global boolean/coded value maps ──────────────────────────────────────────
const BOOL_MAP = { 0: "Não", "0": "Não", false: "Não", 1: "Sim", "1": "Sim", true: "Sim" };
const ACTIVE_MAP = { 0: "Inactivo", "0": "Inactivo", false: "Inactivo", 1: "Activo", "1": "Activo", true: "Activo" };
const SEX_MAP = { M: "Masculino", m: "Masculino", F: "Feminino", f: "Feminino" };
const MONTHS_PT = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const GLOBAL_VALUE_MAP = {
  forgivefine: BOOL_MAP,
  hasfine: BOOL_MAP,
  newcomer: BOOL_MAP,
  allergicfood: BOOL_MAP,
  allergicmedicine: BOOL_MAP,
  hasdisease: BOOL_MAP,
  approved: BOOL_MAP,
  paid: BOOL_MAP,
  active: ACTIVE_MAP,
  enabled: ACTIVE_MAP,
  sex: SEX_MAP,
  gender: SEX_MAP,
};

// ── Entity-specific value maps ────────────────────────────────────────────────
const PAYMENT_STATUS = { 0: "Não Pago", "0": "Não Pago", 1: "Pago", "1": "Pago", 2: "Parcialmente Pago", "2": "Parcialmente Pago" };
const ACTIVE_STATUS = { 0: "Inactivo", "0": "Inactivo", 1: "Activo", "1": "Activo" };
const REQUEST_STATUS = { 0: "Pendente", "0": "Pendente", 1: "Aprovado", "1": "Aprovado", 2: "Rejeitado", "2": "Rejeitado" };

const ENTITY_VALUE_MAP = {
  Pagamento: { status: PAYMENT_STATUS },
  "Pagamento de Emolumentos": { status: PAYMENT_STATUS },
  Inscrição: { status: ACTIVE_STATUS },
  Matrícula: { status: ACTIVE_STATUS },
  Estudante: { status: ACTIVE_STATUS },
  Requisição: { status: REQUEST_STATUS },
};

// ── Fields modified automatically by the system — ignored in diffs ────────────
const SYSTEM_FIELDS_BY_ENTITY = {
  Pagamento: new Set(["sentmessages"]),
  Inscrição: new Set(["overdueamount"]),
};

function isSystemField(key, entity) {
  const set = entity ? SYSTEM_FIELDS_BY_ENTITY[entity] : null;
  return set ? set.has(normalize(key)) : false;
}

function serializeValue(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// The 4 field-label entries above that mention "Nível"/"Classe"/"Curso"
// (level, corsenumber, corse, nivel) always follow the current sucursal's
// vocabulary instead — listAuditTrail's results are already scoped to the
// logged-in user's own sucursal (no cross-sucursal selector on this
// screen), so getSucursalVocab() is safe to read here the same way the
// other Classe/Nível/Curso screens do (see src/utils/classesLabels.js).
const VOCAB_FIELD_LABELS = {
  curso: { level: "Curso", corsenumber: "Curso", corse: "Curso", nivel: "Curso" },
  classe: { level: "Classe", corsenumber: "Classe", corse: "Classe", nivel: "Classe" },
  nivel: { level: "Nível", corsenumber: "Nível", corse: "Nível", nivel: "Nível" },
};

export function translateFieldLabel(key) {
  const norm = normalize(key);
  const override = VOCAB_FIELD_LABELS[getSucursalVocab()][norm];
  if (override) return override;
  return FIELD_LABELS[norm] || key;
}

function translateValue(key, value, entity) {
  if (value === null || value === undefined || String(value) === "") return null;
  const norm = normalize(key);

  if (norm === "month") {
    const n = parseInt(String(value), 10);
    if (!isNaN(n) && n >= 1 && n <= 12) return MONTHS_PT[n - 1];
  }

  const entityMap = entity ? ENTITY_VALUE_MAP[entity] : null;
  if (entityMap && entityMap[norm] !== undefined) {
    const mapped = entityMap[norm][value];
    if (mapped !== undefined) return mapped;
  }

  if (GLOBAL_VALUE_MAP[norm] !== undefined) {
    const mapped = GLOBAL_VALUE_MAP[norm][value];
    if (mapped !== undefined) return mapped;
  }

  return null;
}

const isIsoDateTime = (str) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
const isIsoDate = (str) => /^\d{4}-\d{2}-\d{2}$/.test(str);

const pad = (n) => String(n).padStart(2, "0");
const formatDateTime = (str) => {
  const d = new Date(str);
  if (isNaN(d.getTime())) return str;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const formatDateOnly = (str) => {
  const d = new Date(`${str}T00:00:00`);
  if (isNaN(d.getTime())) return str;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export function formatFieldValue(key, value, entity) {
  if (value === null || value === undefined) return "—";

  if (typeof value === "object" && !Array.isArray(value)) {
    if (value.description) return value.description;
    if (value.name) return value.name;
    const pairs = Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => `${translateFieldLabel(k)}: ${v}`);
    return pairs.length > 0 ? pairs.join("\n") : "—";
  }

  const str = String(value);
  if (str === "") return "—";

  const translated = translateValue(key, value, entity);
  if (translated !== null) return translated;

  if (str === "true") return "Sim";
  if (str === "false") return "Não";

  if (isIsoDateTime(str)) return formatDateTime(str);
  if (isIsoDate(str)) return formatDateOnly(str);

  return str;
}

export function isBasicField(key, value) {
  if (value === null || value === undefined || value === "") return false;
  const lower = key.toLowerCase();
  if (INTERNAL_FIELD_PREFIXES.some((p) => lower.startsWith(p))) return false;
  if (typeof value === "object" && !Array.isArray(value)) return true;
  const str = String(value);
  if (str.length > 200) return false;
  if (lower === "id" && UUID_RE.test(str)) return false;
  return true;
}

// Every (non-system) key whose value differs between the two images.
export function changedFields(oldImage, newImage, entity) {
  const allKeys = new Set([...Object.keys(oldImage || {}), ...Object.keys(newImage || {})]);
  return Array.from(allKeys).filter(
    (k) => !isSystemField(k, entity) && serializeValue((oldImage || {})[k]) !== serializeValue((newImage || {})[k]),
  );
}

// Whether an "update" record has any actual (non-system) field difference —
// used to hide no-op update records (e.g. a save that touched nothing).
export function hasChanges(oldImage, newImage, entity) {
  return changedFields(oldImage, newImage, entity).length > 0;
}
